// FlashTools - firmware parsing & ECU offset detection.
// Offset auto-detection ported from the original flasher's bin-type logic.

export function crc32(bytes) {
  let c;
  const table = crc32.table || (crc32.table = (() => {
    const t = [];
    for (let n = 0; n < 256; n++) {
      c = n;
      for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      t[n] = c >>> 0;
    }
    return t;
  })());
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) crc = table[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  return ((crc ^ 0xffffffff) >>> 0).toString(16).toUpperCase().padStart(8, '0');
}

// 8-bit Honda-style checksum: ((sum ^ 0xFF) + 1) & 0xFF
export function checksum8BitHonda(bytes, size, start = 0) {
  let sum = 0;
  for (let i = start; i < start + size; i++) sum += bytes[i];
  return ((sum ^ 0xff) + 1) & 0xff;
}

// Auto-detect start & checksum offsets from firmware size (ported from GetOffsetsFromBinType)
export function detectOffsets(size) {
  switch (size) {
    case 49152:  return { start: '4000', checksum: '9FEF', chip: 'Keihin 48K' };
    case 57344:  return { start: '0',    checksum: 'DFEF', chip: 'Keihin 56K' };
    case 65536:
    case 98304:  return { start: '0', checksum: '13BFE', chip: 'Denso 96K (K99)' };
    case 131072: return { start: 'E0000', checksum: 'B000', chip: 'Keihin 128K' };
    case 262144: return { start: '0', checksum: '3FFF8', chip: 'Keihin/Shindengen 256K' };
    case 524288: return { start: '0', checksum: '7FFF8', chip: 'MPC560x 512K' };
    case 1048576: return { start: '0', checksum: 'FFFF8', chip: 'SH705x 1M' };
    default: return { start: '0', checksum: 'Auto', chip: 'Unknown' };
  }
}

function parseIntelHex(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.startsWith(':'));
  let max = 0;
  const records = [];
  let base = 0;
  for (const line of lines) {
    const len = parseInt(line.substr(1, 2), 16);
    const addr = parseInt(line.substr(3, 4), 16);
    const type = parseInt(line.substr(7, 2), 16);
    const data = [];
    for (let i = 0; i < len; i++) data.push(parseInt(line.substr(9 + i * 2, 2), 16));
    if (type === 0x04) base = (data[0] << 24) | (data[1] << 16);
    if (type === 0x00) {
      const abs = base + addr;
      records.push({ abs, data });
      max = Math.max(max, abs + len);
    }
  }
  const bytes = new Uint8Array(max).fill(0xff);
  for (const r of records) bytes.set(r.data, r.abs);
  return bytes;
}

export async function parseFirmware(file) {
  const isHex = /\.(hex|ihex|srec|s19)$/i.test(file.name);
  let bytes;
  if (isHex) {
    const text = await file.text();
    bytes = parseIntelHex(text);
  } else {
    bytes = new Uint8Array(await file.arrayBuffer());
  }
  const size = bytes.length;
  const offsets = detectOffsets(size);
  return {
    name: file.name,
    size,
    bytes,
    crc32: crc32(bytes),
    format: isHex ? 'Intel HEX' : 'Raw Binary',
    startOffset: offsets.start,
    checksumOffset: offsets.checksum,
    chip: offsets.chip,
  };
}

export function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}
